<template>
  <parent>
    <loading-page v-if="loading" />
    <div
      v-else
      class="h-100"
    >
      <validation-observer
        ref="rules"
        v-slot="{ invalid }"
      >
        <form-wrapper
          full-width
          full-footer
        >
          <template v-slot:body>
            <paper>
              <div class="d-flex flex-column flex-md-row">
                <div class="px-2 col-12 col-md-6">
                  <validation-provider
                    #default="{ errors }"
                    name="Name"
                    rules="required"
                  >
                    <input-field
                      name="name"
                      label="Name"
                      label-position="top"
                      :error="errors.length ? errors[0] : null"
                    >
                      <b-form-input
                        id="name"
                        v-model="imagePolicy.name"
                        placeholder="Add name here..."
                        :state="errors.length > 0 ? false : null"
                      />
                    </input-field>
                  </validation-provider>

                  <b-form-group
                    label="Comment"
                    label-for="comment"
                  >
                    <b-form-input
                      id="comment"
                      v-model="imagePolicy.comment"
                      placeholder="Add comment here..."
                    />
                  </b-form-group>
                </div>
                <div class="px-2 col-12 col-md-6">
                  <custom-alert
                    icon-name="InfoAlt"
                    items-start
                    variant="alert alert-dark"
                    text-class="text-secondary"
                  ><div class="px-1">
                    <p class="pt-0">
                      To use an Image Preset within your URL, you can easily include the preset name as a query parameter. For example:
                      <span><a href="https://cdn.gjirafa.tech/image?preset=mobile">https://cdn.gjirafa.tech/image?preset=mobile</a></span>
                    </p>
                    <p class="mt-1">
                      The CDN will then apply the processing options defined in the "my-preset" preset to the image.
                    </p>
                  </div>
                  </custom-alert>
                </div>
              </div>

              <div
                class="customize-div d-flex flex-column flex-md-row align-items-start justify-content-start p-2 pt-0 pt-md-2 col-12"
              >
                <div
                  class="border-top w-full col-12 col-md-2 d-flex flex-row flex-md-column justify-content-start align-items-start pt-md-2 pr-md-1 pl-md-0"
                >
                  <div class="d-flex flex-row flex-md-column h-auto justify-content-between align-items-center w-100 col-12 my-1 mt-md-0 p-0">
                    <div
                      class="mobile-link-tabs d-flex justify-content-center align-items-center col-3 col-md-12 m-0 mb-md-1"
                      :class="{'border border-primary rounded': activeTab === 'resize', 'border rounded': activeTab !== 'resize'}"
                      @click="activeTab = 'resize'"
                    >
                      <GjIcon
                        class="icon-mobile m-auto m-md-0 h-auto w-10"
                        :class="{'text-primary': activeTab === 'resize', 'text-secondary': activeTab !== 'resize' }"

                        name="Fullscreen"
                      />
                      <span
                        class="link-label d-none ml-1"
                        :class="{'text-primary': activeTab === 'resize','text-secondary': activeTab !== 'resize'}"
                      >Resize</span>
                    </div>
                    <div
                      class=" mobile-link-tabs d-flex justify-content-center align-items-center col-3 col-md-12 m-0 mb-md-1"
                      :class="{'border border-primary rounded': activeTab === 'filters', 'border rounded': activeTab !== 'filters'}"
                      @click="activeTab = 'filters'"
                    >
                      <GjIcon
                        class="icon-mobile m-auto m-md-0 h-auto w-10"
                        :class="{'text-primary': activeTab === 'filters', 'text-secondary': activeTab !== 'filters'}"
                        name="Filters"
                      />
                      <span
                        class="link-label d-none ml-1"
                        :class="{'text-primary': activeTab === 'filters','text-secondary': activeTab !== 'filters'}"
                      >Filters</span>
                    </div>
                    <div
                      class=" mobile-link-tabs d-flex justify-content-center  align-items-center col-3 col-md-12 m-0"
                      :class="{'border border-primary rounded': activeTab === 'outputSettings', 'border rounded': activeTab !== 'outputSettings'}"
                      @click="activeTab = 'outputSettings'"
                    >
                      <GjIcon
                        class="icon-mobile m-auto m-md-0 h-auto w-10"
                        :class="{'text-primary': activeTab === 'outputSettings', 'text-secondary': activeTab !== 'outputSettings'}"
                        name="Settings"
                      />
                      <span
                        class="link-label d-none ml-1"
                        :class="{'text-primary': activeTab === 'outputSettings','text-secondary': activeTab !== 'outputSettings'}"
                      >Output Settings</span>
                    </div>
                  </div>
                </div>
                <div
                  class="border-top img-preview-wrapper d-flex flex-column justify-content-center align-items-center p-2 col-12 col-md-7 h-auto w-full "
                >
                  <div
                    class="image-container"
                  >
                    <img
                      :src="hasImageError ? `${defaultImage}` : `${defaultImage}${queryString}`"
                      alt="Robot Arm"
                      class="preview-img"
                      @error="hasImageError = true"
                    >
                  </div>

                </div>
                <div
                  v-if="activeTab === 'resize'"
                  class="border-top options-border d-flex flex-column justify-content-center align-items-center col-12 col-md-3 px-0 w-full pt-2"
                >
                  <validation-provider
                    #default="{ errors }"
                    name="width"
                    class="w-100 px-1"
                    :rules="{
                      integer: true,
                      between: [0, 16000],
                      sumVal: {
                        value2: imagePolicy.processingOptions.height,
                        limit: 16000
                      }
                    }"
                  >
                    <input-field
                      label="Width"
                      label-position="top"
                      :error="errors.length ? errors[0] : null"
                    >
                      <b-form-input
                        id="width"
                        v-model="imagePolicy.processingOptions.width"
                        placeholder="Between: 0,16000"
                        class="preset-input"
                        :state="errors.width ? false : null"
                        type="number"
                        inputmode="numeric"
                        @input="hasImageError = false"
                      />
                    </input-field>
                  </validation-provider>

                  <validation-provider
                    #default="{ errors }"
                    name="height"
                    class="w-100 px-1"
                    :rules="{
                      integer: true,
                      between: [0, 16000],
                      sumVal: {
                        value2: imagePolicy.processingOptions.width,
                        limit: 16000
                      }
                    }"
                  >
                    <input-field
                      label="Height"
                      label-position="top"
                      :error="errors.length ? errors[0] : null"
                    >
                      <b-form-input
                        id="height"
                        v-model="imagePolicy.processingOptions.height"
                        placeholder="Between: 0,16000"
                        class="preset-input"
                        :state="errors.height ? false : null"
                        type="number"
                        inputmode="numeric"
                        @input="hasImageError = false"
                      />
                    </input-field>
                  </validation-provider>

                  <validation-provider
                    #default="{ errors }"
                    name="Resize"
                    class="w-100 px-1"
                  >
                    <input-field
                      name="resize"
                      label="Resize"
                      label-position="top"
                      :error="errors.length ? errors[0] : null"
                    >
                      <b-form-select
                        v-model="imagePolicy.processingOptions.resize"
                        :state="errors.length > 0 ? false : null"
                        class="preset-input"
                        @input="hasImageError = false"
                      >
                        <option
                          value=""
                          disabled
                        >
                          Please select an option
                        </option>
                        <option
                          v-for="option in resizeOptions"
                          :key="option.value"
                          :value="option.value"
                        >
                          {{ option.label }}
                        </option>
                      </b-form-select>
                    </input-field>
                  </validation-provider>
                  <validation-provider
                    id="gravity"
                    #default="{ errors }"
                    name="Gravity"
                    class="w-100 px-1"
                    :class="{'d-none': imagePolicy.processingOptions.resize === 'fit'}"
                  >
                    <input-field
                      name="gravity"
                      label="Gravity"
                      label-position="top"
                      :error="errors.length ? errors[0] : null"
                    >
                      <b-form-select
                        v-model="imagePolicy.processingOptions.gravity"
                        :state="errors.length > 0 ? false : null"
                        class="preset-input"
                        @input="hasImageError = false"
                      >
                        <option
                          value=""
                          disabled
                        >
                          Please select an option
                        </option>
                        <option
                          v-for="option in gravityOptions"
                          :key="option.value"
                          :value="option.value"
                        >
                          {{ option.label }}
                        </option>
                      </b-form-select>
                    </input-field>
                  </validation-provider>
                  <validation-provider
                    #default="{ errors }"
                    name="Enlarge"
                    class="px-1"
                  >
                    <label class="d-flex flex-row text-bold "><b-form-checkbox
                      v-model="imagePolicy.processingOptions.enlarge"
                      :options="enlargeOptions"
                      class="preset-input cursor-pointer"
                      :error="errors.length ? errors[0] : null"
                      @input="hasImageError = false"
                    />Enlarge</label>
                    <p
                      class="text-sm text-secondary"
                    >
                      If the width is higher than the original image should we enlarge the image
                    </p>

                  </validation-provider>
                </div>
                <div
                  v-if="activeTab === 'filters'"
                  class="border-top d-flex flex-column justify-content-center align-items-center col-12 col-md-3 px-0 w-full pt-2"
                  active
                >
                  <validation-provider
                    v-slot="{ errors }"
                    name="Blur"
                    class="w-100 px-1"
                    rules="positive|between:0,10"
                  >
                    <input-field
                      label="Blur"
                      label-position="top"
                      :error="errors.length ? errors[0] : null"
                    >
                      <b-form-input
                        id="blur"
                        v-model="imagePolicy.processingOptions.blur"
                        class="preset-input"
                        type="number"
                        inputmode="numeric"
                        placeholder="0.0 - 10.0"
                        :state="errors.length > 0 ? false : null"
                        @input="hasImageError = false"
                      />
                    </input-field>
                  </validation-provider>
                  <validation-provider
                    v-slot="{ errors }"
                    name="sharpen"
                    class="w-100 px-1"
                    rules="positive|between:0,10"
                  >
                    <input-field
                      label="Sharpen"
                      label-position="top"
                      :error="errors.length ? errors[0] : null"
                    >
                      <b-form-input
                        id="sharpen"
                        v-model="imagePolicy.processingOptions.sharpen"
                        type="number"
                        inputmode="numeric"
                        class="preset-input"
                        placeholder="0.0 - 10.0"
                        :state="errors.length > 0 ? false : null"
                        @input="hasImageError = false"
                      />
                    </input-field>
                  </validation-provider>
                </div>
                <div
                  v-if="activeTab === 'outputSettings'"
                  class="border-top d-flex flex-column justify-content-center align-items-center col-12 col-md-3 px-0 w-full pt-2"
                  active
                >
                  <validation-provider
                    #default="{ errors }"
                    name="quality"
                    class="w-100 mb-1 px-1"
                    rules="integer|between:0,100"
                  >
                    <input-field
                      label="Quality"
                      label-position="top"
                      :error="errors.length ? errors[0] : null"
                      @input="hasImageError = false"
                    >
                      <b-form-input
                        id="quality"
                        v-model="imagePolicy.processingOptions.quality"
                        placeholder="Between: 0,100"
                        class="preset-input"
                        type="number"
                        inputmode="numeric"
                        :state="errors.length > 0 ? false : null"
                      />
                    </input-field>
                  </validation-provider>
                  <validation-provider
                    v-slot="{ errors }"
                    name="Format"
                    rules=""
                    class="w-100 px-1"
                  >
                    <input-field
                      name="format"
                      label="Format"
                      label-position="top"
                      :error="errors.length ? errors[0] : null"
                      @input="hasImageError = false"
                    >
                      <b-form-select
                        v-model="imagePolicy.processingOptions.format"
                        :state="errors.length > 0 ? false : null"
                        class="preset-input"
                      >
                        <option
                          value=""
                          disabled
                        >
                          Please select a format
                        </option>
                        <option
                          v-for="format in formatOptions"
                          :key="format.value"
                          :value="format.value"
                        >
                          {{ format.label }}
                        </option>
                      </b-form-select>
                    </input-field>
                  </validation-provider>
                </div>
              </div>
            </paper>

          </template>
          <template v-slot:footer>

            <b-button
              variant="bg-white"
              @click="$emit('close')"
            >
              Cancel
            </b-button>
            <button-loading
              v-if="details"
              :spinning="updatingPolicy"
              variant="bg-primary"
              class="ml-16"
              :disabled="invalid"
              @click="onEdit"
            >
              Update
            </button-loading>
            <button-loading
              v-if="!details"
              :spinning="addingPolicy"
              variant="primary"
              class="ml-16"
              :disabled="invalid"
              @click="onCreate"
            >
              Create
            </button-loading>
          </template>
        </form-wrapper>
      </validation-observer>
    </div>
  </parent>
</template>
<script>

import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  required, integer, positive, sumVal,
} from '@validations'

export default {
  name: 'ImagePoliciesFrom',
  components: {
    InputField: () => import('@/components/form/InputField.vue'),
    Paper: () => import('@/components/paper/Paper.vue'),
    CustomAlert: () => import('@/components/alert/CustomAlert.vue'),
    ValidationProvider,
    ValidationObserver,
  },
  props: {
    row: {
      default: null,
      type: Object,
    },
    details: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      required,
      integer,
      positive,
      sumVal,
      hasImageError: false,
      defaultImage: 'https://jroh9k023r.gjirafa.net/captain/images/optimizer/robot_arm.png',
      enlargeOptions: [
        { value: true, text: 'True' },
        { value: false, text: 'False' },
      ],
      activeTab: 'resize',
      openInfo: false,
      formatOptions: [
        { value: '', label: 'None' },
        { value: 'webp', label: 'WebP' },
        { value: 'jpeg', label: 'JPEG' },
        { value: 'png', label: 'PNG' },
        { value: 'gif', label: 'GIF' },
        { value: 'avif', label: 'AVIF' },
      ],
      resizeOptions: [
        { value: '', label: 'None' },
        { value: 'auto', label: 'Auto' },
        { value: 'fit', label: 'Fit' },
        { value: 'fill', label: 'Fill' },
      ],
      gravityOptions: [
        { label: 'None', value: '' },
        { label: 'Smart', value: 'sm' },
        { label: 'Center', value: 'ce' },
        { label: 'North (top edge)', value: 'no' },
        { label: 'South (bottom edge)', value: 'so' },
        { label: 'East (right edge)', value: 'ea' },
        { label: 'West (left edge)', value: 'we' },
        { label: 'North-east (top-right corner)', value: 'noea' },
        { label: 'North-west (top-left corner)', value: 'nowe' },
        { label: 'South-east (bottom-right corner)', value: 'soea' },
        { label: 'South-west (bottom-left corner)', value: 'sowe' },
      ],

    }
  },

  computed: {
    queryString() {
      if (this.hasImageError) {
        return ''
      }
      const { width, height, gravity } = this.imagePolicy.processingOptions

      const sum = Number(width) + Number(height)

      const params = {
        r: this.imagePolicy.processingOptions.resize,
        el: this.imagePolicy.processingOptions.enlarge,
        bl: this.imagePolicy.processingOptions.blur > 10 ? 10 : this.imagePolicy.processingOptions.blur,
        sh: this.imagePolicy.processingOptions.sharpen > 10 ? 10 : this.imagePolicy.processingOptions.sharpen,
        q: this.imagePolicy.processingOptions.quality > 100 ? 100 : this.imagePolicy.processingOptions.quality,
        format: this.imagePolicy.processingOptions.format,
      }
      if (sum <= 16000) {
        params.w = width
        params.h = height
      }

      if (this.imagePolicy.processingOptions.resize !== 'fit') {
        params.g = gravity
      }
      const queryString = Object.entries(params)
        // eslint-disable-next-line no-unused-vars
        .filter(([key, value]) => value !== undefined && value !== null && value !== '' && value !== 0)
        .map(([key, value]) => `${key}=${value}`)
        .join('&')
      return `?${queryString}`
    },
    imagePolicy() {
      return this.$store.state.imagePolicies.imagePolicy
    },
    loading() {
      return this.$store.state.imagePolicies.gettingPolicy
    },
    addingPolicy() {
      return this.$store.state.imagePolicies.addingPolicy
    },
    updatingPolicy() {
      return this.$store.state.imagePolicies.updatingPolicy
    },
  },
  methods: {
    onCreate() {
      this.validation()
        .then(() => {
          const localImagePolicy = {
            id: this.imagePolicy.id,
            name: this.imagePolicy.name,
            comment: this.imagePolicy.comment,
            processingOptions: {
              width: parseInt(this.imagePolicy.processingOptions.width, 10),
              height: parseInt(this.imagePolicy.processingOptions.height, 10),
              resize: this.imagePolicy.processingOptions.resize,
              gravity: this.imagePolicy.processingOptions.resize === 'fit' ? '' : this.imagePolicy.processingOptions.gravity,
              enlarge: this.imagePolicy.processingOptions.enlarge,
              blur: parseFloat(this.imagePolicy.processingOptions.blur),
              sharpen: parseFloat(this.imagePolicy.processingOptions.sharpen),
              quality: parseInt(this.imagePolicy.processingOptions.quality, 10),
              format: this.imagePolicy.processingOptions.format,
            },
          }
          this.$store
            .dispatch('imagePolicies/addImagePolicy', localImagePolicy)
            .then(() => {
              this.successToast('Image policy was added')
              this.$emit('close')
              this.$emit('onCreate')
            })
            .catch(error => {
              this.errorToast(error.response?.data?.errors[0] || 'Something went wrong while creating policy.')
            })
        })
        .catch(() => {})
    },

    onEdit() {
      this.validation()
        .then(() => {
          const localImagePolicy = {
            id: this.imagePolicy.id,
            name: this.imagePolicy.name,
            comment: this.imagePolicy.comment,
            processingOptions: {
              width: parseInt(this.imagePolicy.processingOptions.width, 10),
              height: parseInt(this.imagePolicy.processingOptions.height, 10),
              resize: this.imagePolicy.processingOptions.resize,
              gravity: this.imagePolicy.processingOptions.resize === 'fit' ? '' : this.imagePolicy.processingOptions.gravity,
              enlarge: this.imagePolicy.processingOptions.enlarge,
              blur: parseFloat(this.imagePolicy.processingOptions.blur),
              sharpen: parseFloat(this.imagePolicy.processingOptions.sharpen),
              quality: parseInt(this.imagePolicy.processingOptions.quality, 10),
              format: this.imagePolicy.processingOptions.format,
            },
          }
          this.$emit('edit', localImagePolicy)
        })
        .catch(() => {})
    },
    validation() {
      return new Promise((resolve, reject) => {
        this.$refs.rules.validate().then(success => {
          if (success) {
            resolve(true)
          } else {
            reject()
          }
        })
      })
    },
  },

}
</script>
<style lang="scss" scoped>

.preview-img{
  max-width: 100%;
  max-height: 480px;
}

.preset-input{
  max-height:2.5rem !important;
}

.img-preview-wrapper {
  border-left: 1px solid #E0E5EB;
  min-height: 480px;
}

.mobile-link-tabs{
  min-height: 37px;

  &:hover{
    cursor: pointer;
    border: 1px solid #6ea4ea !important;
  }
}

.icon-mobile{
  min-height: 24px;
  min-width: 24px;
}

@media (min-width: 926px)  {
  .link-label{
    display: block !important;
  }
  .mobile-link-tabs{
    justify-content: start !important;
  }
}

@media (max-width: 767px)  {

  .mobile-link-tabs{
    padding: 0.4rem;
    margin: 1rem;
    min-height: unset;
  }

  .img-preview-wrapper{
    min-height: unset;
  }
}
</style>
